// Small DOM actions shared by the UI components.

/** Move the node to <body> so popovers and dialogs are never clipped. */
export function portal(node: HTMLElement, target: HTMLElement | string = 'body') {
  const host = typeof target === 'string' ? document.querySelector(target) : target;
  host?.appendChild(node);
  return {
    destroy() {
      node.remove();
    }
  };
}

/** Call `handler` on a pointer-down outside the node (and outside `ignore`). */
export function clickOutside(node: HTMLElement, params: { handler: () => void; ignore?: (HTMLElement | null | undefined)[] }) {
  let current = params;
  const onDown = (event: PointerEvent) => {
    const target = event.target as Node;
    if (node.contains(target)) return;
    if (current.ignore?.some((el) => el && el.contains(target))) return;
    current.handler();
  };
  document.addEventListener('pointerdown', onDown, true);
  return {
    update(next: typeof params) {
      current = next;
    },
    destroy() {
      document.removeEventListener('pointerdown', onDown, true);
    }
  };
}

/** Keep focus inside the node while it is open (dialogs). */
export function trapFocus(node: HTMLElement) {
  const previous = document.activeElement as HTMLElement | null;
  const selector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const focusables = () => Array.from(node.querySelectorAll<HTMLElement>(selector)).filter((el) => el.offsetParent !== null);
  queueMicrotask(() => {
    const auto = node.querySelector<HTMLElement>('[autofocus],[data-autofocus]');
    (auto || focusables()[0] || node).focus({ preventScroll: true });
  });
  const onKey = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const items = focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  node.addEventListener('keydown', onKey);
  return {
    destroy() {
      node.removeEventListener('keydown', onKey);
      previous?.focus?.({ preventScroll: true });
    }
  };
}

/** Position a floating element next to an anchor, flipping when out of room. */
export function anchored(node: HTMLElement, params: { anchor: HTMLElement | null | undefined; placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'right-start'; offset?: number; matchWidth?: boolean }) {
  let current = params;
  const place = () => {
    const anchor = current.anchor;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const gap = current.offset ?? 6;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    node.style.position = 'fixed';
    if (current.matchWidth) node.style.minWidth = `${r.width}px`;
    const w = node.offsetWidth;
    const h = node.offsetHeight;
    const placement = current.placement || 'bottom-start';
    let top: number;
    let left: number;
    if (placement === 'right-start') {
      left = r.right + gap;
      top = r.top;
      if (left + w > vw - 8) left = r.left - w - gap;
    } else {
      const below = r.bottom + gap;
      const above = r.top - gap - h;
      const wantTop = placement.startsWith('top');
      top = wantTop ? (above > 8 ? above : below) : below + h > vh - 8 && above > 8 ? above : below;
      left = placement.endsWith('end') ? r.right - w : r.left;
    }
    left = Math.max(8, Math.min(left, vw - w - 8));
    top = Math.max(8, Math.min(top, vh - h - 8));
    node.style.top = `${Math.round(top)}px`;
    node.style.left = `${Math.round(left)}px`;
  };
  const ro = new ResizeObserver(place);
  ro.observe(node);
  window.addEventListener('scroll', place, true);
  window.addEventListener('resize', place);
  requestAnimationFrame(place);
  return {
    update(next: typeof params) {
      current = next;
      place();
    },
    destroy() {
      ro.disconnect();
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    }
  };
}

/**
 * Lightweight tooltip: <button use:tooltip={'Text'}>.
 * The text also becomes the aria-label when the node has none (icon buttons).
 * For nodes with visible text pass { text, describe: true }: the text is then
 * exposed as aria-description and the visible label stays the name.
 */
export type TooltipParam = string | null | undefined | { text: string | null | undefined; describe?: boolean };

export function tooltip(node: HTMLElement, param: TooltipParam) {
  let tip: HTMLDivElement | null = null;
  let label: string | null | undefined = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const apply = (next: TooltipParam) => {
    const text = typeof next === 'object' && next !== null ? next.text : next;
    const describe = typeof next === 'object' && next !== null && !!next.describe;
    label = text;
    if (!text) return;
    if (describe) node.setAttribute('aria-description', text);
    else if (!node.getAttribute('aria-label')) node.setAttribute('aria-label', text);
  };
  const show = () => {
    if (!label || tip) return;
    timer = setTimeout(() => {
      tip = document.createElement('div');
      tip.className = 'tp-tooltip';
      tip.setAttribute('role', 'tooltip');
      tip.textContent = label || '';
      document.body.appendChild(tip);
      const r = node.getBoundingClientRect();
      const w = tip.offsetWidth;
      let left = r.left + r.width / 2 - w / 2;
      left = Math.max(6, Math.min(left, window.innerWidth - w - 6));
      let top = r.top - tip.offsetHeight - 8;
      if (top < 6) top = r.bottom + 8;
      tip.style.left = `${left}px`;
      tip.style.top = `${top}px`;
    }, 350);
  };
  const hide = () => {
    if (timer) clearTimeout(timer);
    tip?.remove();
    tip = null;
  };
  apply(param);
  node.addEventListener('pointerenter', show);
  node.addEventListener('pointerleave', hide);
  node.addEventListener('focus', show);
  node.addEventListener('blur', hide);
  node.addEventListener('pointerdown', hide);
  return {
    update(next: TooltipParam) {
      apply(next);
    },
    destroy() {
      hide();
      node.removeEventListener('pointerenter', show);
      node.removeEventListener('pointerleave', hide);
      node.removeEventListener('focus', show);
      node.removeEventListener('blur', hide);
      node.removeEventListener('pointerdown', hide);
    }
  };
}
